'use client';

import { c as _c } from "react/compiler-runtime";
import type * as React from 'react';
import type { ReactNode } from 'react';
import { DropdownMenu, DropdownMenuGroup, DropdownMenuLabel, DropdownMenuPortal, DropdownMenuRadioGroup, DropdownMenuSeparator, DropdownMenuShortcut, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuTrigger, DropdownMenuCheckboxItem as ShadcnDropdownMenuCheckboxItem, DropdownMenuContent as ShadcnDropdownMenuContent, DropdownMenuItem as ShadcnDropdownMenuItem, DropdownMenuRadioItem as ShadcnDropdownMenuRadioItem, DropdownMenuSubContent as ShadcnDropdownMenuSubContent } from '@/components/ui/dropdown-menu';
import type { IconSvgElement } from '@/components/ui/icon';
import { Icon } from '@/components/ui/icon';
import { DROPDOWN_ANIM } from '@/lib/ui/animation';
import { cn } from '@/lib/ui/utils';

// Re-export compound primitives — see app-dialog.tsx for migration rationale.
export { DropdownMenu, DropdownMenuGroup, DropdownMenuLabel, DropdownMenuPortal, DropdownMenuRadioGroup, DropdownMenuSeparator, DropdownMenuShortcut, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuTrigger };

// ---------------------------------------------------------------------------
// Wrapped sub-components — layer Morfi tokens (z-popover above z-modal dialogs,
// text-body, DROPDOWN_ANIM, will-change-transform) over stock primitives.
// dropdown-menu.tsx itself is byte-identical to canonical stock output (FQ-111).
// ---------------------------------------------------------------------------

const DROPDOWN_CONTENT_MORFI_TOKENS = 'z-popover rounded-overlay shadow-elevation-popover will-change-transform' as const;
const DROPDOWN_ITEM_TOKENS = 'rounded-overlay-item text-body' as const;
type DropdownMenuContentProps = React.ComponentProps<typeof ShadcnDropdownMenuContent>;
type DropdownMenuSubContentProps = React.ComponentProps<typeof ShadcnDropdownMenuSubContent>;
type DropdownMenuItemProps = React.ComponentProps<typeof ShadcnDropdownMenuItem>;
type DropdownMenuCheckboxItemProps = React.ComponentProps<typeof ShadcnDropdownMenuCheckboxItem>;
type DropdownMenuRadioItemProps = React.ComponentProps<typeof ShadcnDropdownMenuRadioItem>;
export function DropdownMenuContent(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn(DROPDOWN_CONTENT_MORFI_TOKENS, DROPDOWN_ANIM, className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <ShadcnDropdownMenuContent className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
export function DropdownMenuSubContent(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn(DROPDOWN_CONTENT_MORFI_TOKENS, DROPDOWN_ANIM, className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <ShadcnDropdownMenuSubContent className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
export function DropdownMenuItem(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn(DROPDOWN_ITEM_TOKENS, className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <ShadcnDropdownMenuItem className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
export function DropdownMenuCheckboxItem(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn(DROPDOWN_ITEM_TOKENS, className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <ShadcnDropdownMenuCheckboxItem className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
export function DropdownMenuRadioItem(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn(DROPDOWN_ITEM_TOKENS, className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <ShadcnDropdownMenuRadioItem className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}

// ---------------------------------------------------------------------------
// Item types — discriminated union
// ---------------------------------------------------------------------------

export type DropdownItem = {
  key?: string;
  type?: 'item' | undefined;
  label: ReactNode;
  icon?: IconSvgElement | undefined;
  shortcut?: ReactNode | undefined;
  disabled?: boolean | undefined;
  variant?: 'default' | 'destructive' | undefined;
  onSelect?: (() => void) | undefined;
};
export type DropdownSeparator = {
  type: 'separator';
  key: string;
};
export type DropdownLabel = {
  type: 'label';
  label: ReactNode;
  key?: string;
};
export type DropdownSubmenu = {
  key?: string;
  type: 'submenu';
  label: ReactNode;
  icon?: IconSvgElement | undefined;
  items: ReadonlyArray<DropdownEntry>;
};
export type DropdownCheckboxItem = {
  key?: string;
  type: 'checkbox';
  label: ReactNode;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean | undefined;
};
export type DropdownRadioGroup = {
  key?: string;
  type: 'radio-group';
  value: string;
  onValueChange: (value: string) => void;
  items: ReadonlyArray<{
    value: string;
    label: ReactNode;
    disabled?: boolean | undefined;
  }>;
};
export type DropdownEntry = DropdownItem | DropdownSeparator | DropdownLabel | DropdownSubmenu | DropdownCheckboxItem | DropdownRadioGroup;

// ---------------------------------------------------------------------------
// AppDropdownMenu
// ---------------------------------------------------------------------------

export interface AppDropdownMenuProps {
  trigger: ReactNode;
  items: ReadonlyArray<DropdownEntry>;
  open?: boolean | undefined;
  onOpenChange?: ((open: boolean) => void) | undefined;
  side?: 'top' | 'right' | 'bottom' | 'left' | undefined;
  align?: 'start' | 'center' | 'end' | undefined;
  contentClassName?: string | undefined;
}
interface AppDropdownMenuComponent {
  (props: AppDropdownMenuProps): React.ReactNode;
  Trigger: typeof DropdownMenuTrigger;
  Content: typeof DropdownMenuContent;
  Item: typeof DropdownMenuItem;
  Separator: typeof DropdownMenuSeparator;
}
function isSeparator(entry: DropdownEntry): entry is DropdownSeparator {
  return (entry as DropdownSeparator).type === 'separator';
}
function isLabel(entry: DropdownEntry): entry is DropdownLabel {
  return (entry as DropdownLabel).type === 'label';
}
function isSubmenu(entry: DropdownEntry): entry is DropdownSubmenu {
  return (entry as DropdownSubmenu).type === 'submenu';
}
function isCheckbox(entry: DropdownEntry): entry is DropdownCheckboxItem {
  return (entry as DropdownCheckboxItem).type === 'checkbox';
}
function isRadioGroup(entry: DropdownEntry): entry is DropdownRadioGroup {
  return (entry as DropdownRadioGroup).type === 'radio-group';
}
function isItem(entry: DropdownEntry): entry is DropdownItem {
  return entry.type === undefined || entry.type === 'item';
}
function labelKey(label: ReactNode): string {
  return typeof label === 'string' || typeof label === 'number' ? String(label) : 'custom-label';
}
function entryKey(entry: DropdownEntry): string {
  if (isSeparator(entry)) return entry.key;
  if (entry.key !== undefined) return entry.key;
  if (isRadioGroup(entry)) return `radio-${entry.items.map(item => item.value).join('-')}`;
  return `${entry.type ?? 'item'}-${labelKey(entry.label)}`;
}
function renderEntries(entries: ReadonlyArray<DropdownEntry>, keyPrefix: string = ''): ReactNode {
  return entries.map((entry): ReactNode => {
    const key = `${keyPrefix}${entryKey(entry)}`;
    if (isSeparator(entry)) {
      return <DropdownMenuSeparator key={key} />;
    }
    if (isLabel(entry)) {
      return <DropdownMenuLabel key={key}>{entry.label}</DropdownMenuLabel>;
    }
    if (isSubmenu(entry)) {
      return <DropdownMenuSub key={key}>
          <DropdownMenuSubTrigger>
            {entry.icon !== undefined && <Icon icon={entry.icon} className="mr-2 size-4" />}
            {entry.label}
          </DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent>{renderEntries(entry.items, `${key}/`)}</DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>;
    }
    if (isCheckbox(entry)) {
      return <DropdownMenuCheckboxItem key={key} checked={entry.checked} onCheckedChange={entry.onCheckedChange} {...entry.disabled !== undefined ? {
        disabled: entry.disabled
      } : {}}>
          {entry.label}
        </DropdownMenuCheckboxItem>;
    }
    if (isRadioGroup(entry)) {
      return <DropdownMenuRadioGroup key={key} value={entry.value} onValueChange={entry.onValueChange}>
          {entry.items.map(radio => <DropdownMenuRadioItem key={radio.value} value={radio.value} {...radio.disabled !== undefined ? {
          disabled: radio.disabled
        } : {}}>
              {radio.label}
            </DropdownMenuRadioItem>)}
        </DropdownMenuRadioGroup>;
    }
    if (isItem(entry)) {
      return <DropdownMenuItem key={key} {...entry.disabled !== undefined ? {
        disabled: entry.disabled
      } : {}} {...entry.variant !== undefined ? {
        variant: entry.variant
      } : {}} {...entry.onSelect !== undefined ? {
        onSelect: entry.onSelect
      } : {}}>
          {entry.icon !== undefined && <Icon icon={entry.icon} />}
          <span>{entry.label}</span>
          {entry.shortcut !== undefined && <span className="ml-auto text-caption text-foreground-secondary">{entry.shortcut}</span>}
        </DropdownMenuItem>;
    }
    return null;
  });
}
function AppDropdownMenuImpl(t0) {
  const $ = _c(24);
  const {
    trigger,
    items,
    open,
    onOpenChange,
    side,
    align,
    contentClassName
  } = t0;
  let t1;
  if ($[0] !== open) {
    t1 = open !== undefined ? {
      open
    } : {};
    $[0] = open;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== onOpenChange) {
    t2 = onOpenChange !== undefined ? {
      onOpenChange
    } : {};
    $[2] = onOpenChange;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== trigger) {
    t3 = <DropdownMenuTrigger asChild={true}>{trigger}</DropdownMenuTrigger>;
    $[4] = trigger;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== side) {
    t4 = side !== undefined ? {
      side
    } : {};
    $[6] = side;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== align) {
    t5 = align !== undefined ? {
      align
    } : {};
    $[8] = align;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== contentClassName) {
    t6 = cn(contentClassName);
    $[10] = contentClassName;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== items) {
    t7 = renderEntries(items);
    $[12] = items;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t4 || $[15] !== t5 || $[16] !== t6 || $[17] !== t7) {
    t8 = <DropdownMenuContent {...t4} {...t5} className={t6}>{t7}</DropdownMenuContent>;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t1 || $[20] !== t2 || $[21] !== t3 || $[22] !== t8) {
    t9 = <DropdownMenu {...t1} {...t2}>{t3}{t8}</DropdownMenu>;
    $[19] = t1;
    $[20] = t2;
    $[21] = t3;
    $[22] = t8;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  return t9;
}
export const AppDropdownMenu: AppDropdownMenuComponent = Object.assign(AppDropdownMenuImpl, {
  Trigger: DropdownMenuTrigger,
  Content: DropdownMenuContent,
  Item: DropdownMenuItem,
  Separator: DropdownMenuSeparator
});